import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
} from "firebase/firestore";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { db, auth, aiSupportChat } from "../../firebase";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistProfile,
} from "../../store/slices/authSlice";
import { formatTime, formatHebrewDate } from "../../utils/dateUtils";
import { LENNY_OPEN_EVENT } from "./lennyBus";
import ModalCloseButton from "../ui/ModalCloseButton";
import lennyImage from "../../assets/lenny_side_hey.png";
import lennyIcon from "../../assets/lenny-icon-512.jpg";
import lennyRightHey from "../../assets/lenny_right_side_hey.png";
import ContentLoader from "../shared/ContentLoader";
import "./SupportChatWidget.css";

const PAGE_SIZE = 10;
const MAX_QUESTION_CHARS = 1000;
const COOLDOWN_MS = 6000;
const SUPPORT_WHATSAPP_URL = "https://wa.me/972543073737";
const SUPPORT_PAGE_PATH = "/dashboard/support";

const WELCOME_MESSAGE = {
  id: "lenny-welcome",
  role: "assistant",
  content:
    "היי, אני לני 👋 העוזרת החכמה של CliniKit.\nאפשר לשאול אותי כל שאלה על השימוש במערכת — בעברית או באנגלית — ואשמח לעזור!",
  isWelcome: true,
};

const ERROR_MESSAGES = {
  cooldown: "אנא המתינו מספר שניות בין הודעות ⏱️",
  daily_limit: `הגעתם למגבלת ההודעות היומית. אפשר לנסות שוב מחר, או לפנות אלינו בוואטסאפ: ${SUPPORT_WHATSAPP_URL}`,
  monthly_limit: `מכסת התמיכה החודשית של הקליניקה נוצלה. נשמח לעזור בוואטסאפ: ${SUPPORT_WHATSAPP_URL}`,
  ai_error: "אירעה שגיאה, נסו שוב",
};

// --- Lightweight message renderer: **bold** + links (wa.me / support page) ---

const LINK_SPLIT_REGEX = /(https?:\/\/\S+|\/dashboard\/support)/g;

function renderTextChunk(text, keyPrefix) {
  // **bold** support inside plain chunks
  return text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) => {
    if (chunk.startsWith("**") && chunk.endsWith("**")) {
      return <strong key={`${keyPrefix}-${i}`}>{chunk.slice(2, -2)}</strong>;
    }
    return <span key={`${keyPrefix}-${i}`}>{chunk}</span>;
  });
}

function MessageContent({ content, onInternalLink }) {
  const lines = String(content || "").split("\n");
  return (
    <>
      {lines.map((line, lineIdx) => (
        <div key={lineIdx} className="lenny-msg-line" dir="auto">
          {line.split(LINK_SPLIT_REGEX).map((part, i) => {
            if (!part) return null;
            if (part === SUPPORT_PAGE_PATH) {
              return (
                <button
                  key={i}
                  type="button"
                  className="lenny-inline-link"
                  onClick={() => onInternalLink(SUPPORT_PAGE_PATH)}
                >
                  עמוד התמיכה
                </button>
              );
            }
            if (/^https?:\/\//.test(part)) {
              const isWhatsapp = part.includes("wa.me");
              // strip trailing punctuation that got glued to the URL
              const cleanUrl = part.replace(/[).,،]+$/, "");
              return (
                <a
                  key={i}
                  href={cleanUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lenny-inline-link"
                >
                  {isWhatsapp ? "וואטסאפ 💬" : cleanUrl}
                </a>
              );
            }
            return renderTextChunk(part, `${lineIdx}-${i}`);
          })}
        </div>
      ))}
    </>
  );
}

// --- UI context capture (page / active tabs / open popup) ---
// Sent with each question so Lenny knows where the user currently is.
// Only technical identifiers and static UI labels — never user data.

function getUiContext() {
  const context = { path: window.location.pathname };
  try {
    // Active tabs: house pattern is a button with "tab" in its class + active
    // modifier (e.g. .settings-tab.active, .rp-tab--active), or aria-selected
    const tabEls = document.querySelectorAll(
      '[class*="tab"].active, [class*="tab"][class*="--active"], [aria-selected="true"]'
    );
    const tabs = [];
    tabEls.forEach((el) => {
      if (el.closest("#support-chat-root")) return;
      const label = (el.textContent || "").trim().slice(0, 60);
      if (label && !tabs.includes(label)) tabs.push(label);
    });
    if (tabs.length) context.tabs = tabs.slice(0, 5);

    // Open popup/modal: role="dialog" or house overlay classes. Send only the
    // technical className (may reveal which modal), never its text content.
    const dialogEl = document.querySelector(
      '[role="dialog"]:not(#support-chat-root [role="dialog"]), [class*="modal-overlay"], [class*="-modal "], [class$="-modal"]'
    );
    if (dialogEl && !dialogEl.closest("#support-chat-root")) {
      const cls = String(dialogEl.className || "")
        .split(/\s+/)
        .slice(0, 3)
        .join(" ")
        .slice(0, 80);
      const ariaLabel = dialogEl.getAttribute("aria-label") || "";
      context.popup = (ariaLabel ? `${ariaLabel} (${cls})` : cls).slice(0, 120);
    }
  } catch {
    // context is best-effort — never block sending over it
  }
  return context;
}

// --- Firestore history mapping ---

function mapDoc(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    role: data.role,
    content: data.content,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
    dayKey: data.dayKey,
  };
}

function SupportChatWidget() {
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const therapistProfile = useSelector(selectTherapistProfile);
  const navigate = useNavigate();

  // User-side avatar: clinic logo, falling back to the therapist's initial
  // (same pattern as the DashboardLayout user menu)
  const clinicLogoUrl = clinicProfile?.logoUrl || null;
  const userInitial = therapistProfile?.firstName?.charAt(0) || "U";

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // chronological
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const [errorBubble, setErrorBubble] = useState(null);

  const portalRef = useRef(null);
  const popupRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const cursorRef = useRef(null); // oldest loaded doc snapshot (pagination cursor)
  const lastSendAtRef = useRef(0);

  // Dedicated portal host outside #root — the a11y widget applies filter/zoom
  // to #root, which would break position:fixed descendants (same trick as
  // AccessibilityWidget).
  if (!portalRef.current && typeof document !== "undefined") {
    let node = document.getElementById("support-chat-root");
    if (!node) {
      node = document.createElement("div");
      node.id = "support-chat-root";
      document.body.appendChild(node);
    }
    portalRef.current = node;
  }

  // External open requests (mobile header "תמיכה" button → openLenny()).
  // On mobile the floating FAB is hidden (CSS) so this is the only entry point.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(LENNY_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(LENNY_OPEN_EVENT, onOpen);
  }, []);

  // Mobile full-screen sheet vs. the iOS keyboard: Safari does NOT shrink the
  // layout viewport when the keyboard opens — it scrolls the page so the focused
  // input is visible, which pushes a 100dvh `position:fixed` sheet (and its
  // header with the close button) off the top. Fix: size the sheet to the
  // *visual* viewport and translate it by the viewport's offsetTop so the header
  // stays pinned to the visible top edge; lock body scroll meanwhile so the page
  // underneath can't drift. Desktop/no-visualViewport browsers are untouched.
  useEffect(() => {
    if (!open) return undefined;
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    const isMobileView = () => window.matchMedia("(max-width: 768px)").matches;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevTouch = body.style.touchAction;
    body.style.overflow = "hidden";
    body.style.touchAction = "none";

    let frame = 0;
    const apply = () => {
      frame = 0;
      const el = popupRef.current;
      if (!el) return;
      if (!vv || !isMobileView()) {
        el.style.height = "";
        el.style.transform = "";
        return;
      }
      el.style.height = `${Math.round(vv.height)}px`;
      el.style.transform = `translateY(${Math.round(vv.offsetTop)}px)`;
      // keep the conversation anchored to the newest message as the sheet shrinks
      const list = listRef.current;
      if (list) list.scrollTop = list.scrollHeight;
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(apply);
    };

    apply();
    if (vv) {
      vv.addEventListener("resize", schedule);
      vv.addEventListener("scroll", schedule);
    }
    window.addEventListener("orientationchange", schedule);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      if (vv) {
        vv.removeEventListener("resize", schedule);
        vv.removeEventListener("scroll", schedule);
      }
      window.removeEventListener("orientationchange", schedule);
      body.style.overflow = prevOverflow;
      body.style.touchAction = prevTouch;
    };
  }, [open]);

  const scrollToBottom = useCallback((behavior = "auto") => {
    requestAnimationFrame(() => {
      const el = listRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior });
    });
  }, []);

  // Load last PAGE_SIZE messages on first open
  const loadInitialHistory = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    setLoadingHistory(true);
    try {
      const chatRef = collection(db, "therapists", uid, "supportChat");
      const snap = await getDocs(
        query(chatRef, orderBy("createdAt", "desc"), limit(PAGE_SIZE))
      );
      const loaded = snap.docs.map(mapDoc).reverse();
      cursorRef.current = snap.docs[snap.docs.length - 1] || null;
      setHasMore(snap.docs.length === PAGE_SIZE);
      setMessages(loaded);
      setHistoryLoaded(true);
      scrollToBottom();
    } catch (error) {
      console.error("[CK-8532] Failed loading support chat history", {
        errorCode: "CK-8532",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      setHistoryLoaded(true);
    } finally {
      setLoadingHistory(false);
    }
  }, [scrollToBottom]);

  // Infinite scroll up: prepend PAGE_SIZE older messages, keep scroll position
  const loadMore = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid || !cursorRef.current || loadingMore) return;
    setLoadingMore(true);
    const el = listRef.current;
    const prevScrollHeight = el ? el.scrollHeight : 0;
    try {
      const chatRef = collection(db, "therapists", uid, "supportChat");
      const snap = await getDocs(
        query(
          chatRef,
          orderBy("createdAt", "desc"),
          startAfter(cursorRef.current),
          limit(PAGE_SIZE)
        )
      );
      const older = snap.docs.map(mapDoc).reverse();
      cursorRef.current = snap.docs[snap.docs.length - 1] || cursorRef.current;
      setHasMore(snap.docs.length === PAGE_SIZE);
      if (older.length) {
        setMessages((prev) => [...older, ...prev]);
        requestAnimationFrame(() => {
          if (el) el.scrollTop += el.scrollHeight - prevScrollHeight;
        });
      }
    } catch (error) {
      console.error("[CK-8532] Failed loading older support chat messages", {
        errorCode: "CK-8532",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore]);

  useEffect(() => {
    if (open && !historyLoaded && !loadingHistory) {
      loadInitialHistory();
    }
  }, [open, historyLoaded, loadingHistory, loadInitialHistory]);

  // Esc closes popup
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    // Desktop only — on mobile, auto-focus pops the keyboard before the user
    // has read anything; the input focuses on tap instead.
    if (!window.matchMedia("(max-width: 768px)").matches) {
      inputRef.current?.focus();
    }
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (el && el.scrollTop < 40 && hasMore && !loadingMore && !loadingHistory) {
      loadMore();
    }
  }, [hasMore, loadingMore, loadingHistory, loadMore]);

  const handleInternalLink = useCallback(
    (path) => {
      setOpen(false);
      navigate(path);
    },
    [navigate]
  );

  const handleSend = useCallback(async () => {
    const question = input.trim();
    if (!question || sending || !clinicId) return;

    // Local cooldown mirror (server is the authority)
    if (Date.now() - lastSendAtRef.current < COOLDOWN_MS) {
      setErrorBubble(ERROR_MESSAGES.cooldown);
      return;
    }

    setErrorBubble(null);
    setSending(true);
    setInput("");
    const optimisticId = `pending-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: optimisticId, role: "user", content: question, createdAt: new Date(), pending: true },
    ]);
    scrollToBottom("smooth");

    const result = await aiSupportChat({ clinicId, question, uiContext: getUiContext() });

    if (result.success && result.answer) {
      lastSendAtRef.current = Date.now();
      setMessages((prev) =>
        prev
          .map((m) => (m.id === optimisticId ? { ...m, pending: false } : m))
          .concat({
            id: `answer-${Date.now()}`,
            role: "assistant",
            content: result.answer,
            createdAt: new Date(),
          })
      );
      scrollToBottom("smooth");
    } else {
      // Remove the optimistic bubble, restore the text so the user can retry
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setInput(question);
      setErrorBubble(ERROR_MESSAGES[result.errorType] || ERROR_MESSAGES.ai_error);
    }
    setSending(false);
  }, [input, sending, clinicId, scrollToBottom]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!portalRef.current) return null;

  const displayMessages = messages.length === 0 && historyLoaded ? [WELCOME_MESSAGE] : messages;

  return createPortal(
    <>
      {!open && (
        <button
          type="button"
          className="lenny-fab"
          onClick={() => setOpen(true)}
          aria-label="לני — צ'אט תמיכה"
          title="לני — צ'אט תמיכה"
        >
          <img src={lennyImage} alt="" className="lenny-fab-img" />
        </button>
      )}

      {open && (
        <div
          className="lenny-overlay"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {open && (
        <div
          className="lenny-popup"
          ref={popupRef}
          dir="rtl"
          role="dialog"
          aria-label="צ'אט תמיכה עם לני"
        >
          <div className="lenny-header">
            <img src={lennyRightHey} alt="" className="lenny-avatar-header" />
            <div className="lenny-header-text">
              <span className="lenny-header-name">לני</span>
              <span className="lenny-header-status">
                <span className="lenny-status-dot" />
                העוזרת החכמה של CliniKit
              </span>
            </div>
            <ModalCloseButton onClick={() => setOpen(false)} />
          </div>

          <div className="lenny-messages" ref={listRef} onScroll={handleScroll}>
            {loadingMore && <ContentLoader row size="xs" message="טוען הודעות קודמות…" />}
            {loadingHistory && <ContentLoader row size="xs" message="טוען שיחה…" />}

            {displayMessages.map((msg, idx) => {
              const prev = displayMessages[idx - 1];
              const showDate =
                msg.createdAt &&
                (!prev || !prev.createdAt ||
                  formatHebrewDate(prev.createdAt) !== formatHebrewDate(msg.createdAt));
              return (
                <div key={msg.id}>
                  {showDate && (
                    <div className="lenny-date-separator">
                      <span>{formatHebrewDate(msg.createdAt)}</span>
                    </div>
                  )}
                  <div
                    className={`lenny-msg-row ${
                      msg.role === "assistant" ? "lenny-msg-row-assistant" : "lenny-msg-row-user"
                    }`}
                  >
                    {msg.role === "assistant" && (
                      <img src={lennyIcon} alt="" className="lenny-avatar lenny-avatar-msg" />
                    )}
                    <div
                      className={`lenny-bubble ${
                        msg.role === "assistant" ? "lenny-bubble-assistant" : "lenny-bubble-user"
                      } ${msg.pending ? "lenny-bubble-pending" : ""}`}
                    >
                      <MessageContent content={msg.content} onInternalLink={handleInternalLink} />
                      {msg.createdAt && (
                        <span className="lenny-msg-time">{formatTime(msg.createdAt)}</span>
                      )}
                    </div>
                    {msg.role === "user" &&
                      (clinicLogoUrl ? (
                        <img src={clinicLogoUrl} alt="" className="lenny-avatar lenny-avatar-msg" />
                      ) : (
                        <span className="lenny-avatar lenny-avatar-msg lenny-avatar-initial">
                          {userInitial}
                        </span>
                      ))}
                  </div>
                </div>
              );
            })}

            {sending && (
              <div className="lenny-msg-row lenny-msg-row-assistant">
                <img src={lennyIcon} alt="" className="lenny-avatar lenny-avatar-msg" />
                <div className="lenny-bubble lenny-bubble-assistant lenny-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}

            {errorBubble && (
              <div className="lenny-error-bubble" dir="auto">
                <MessageContent content={errorBubble} onInternalLink={handleInternalLink} />
              </div>
            )}
          </div>

          <div className="lenny-input-row">
            <textarea
              ref={inputRef}
              className="lenny-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="שאלו את לני כל דבר על המערכת…"
              aria-label="הודעה ללני"
              maxLength={MAX_QUESTION_CHARS}
              rows={1}
              dir="auto"
            />
            <button
              type="button"
              className="lenny-send-btn"
              onClick={handleSend}
              disabled={!input.trim() || sending}
              aria-label="שליחה"
            >
              <PaperPlaneTilt size={18} weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>,
    portalRef.current
  );
}

export default SupportChatWidget;
